import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { CustomerFilters } from '@aid/customers/types/classes';
import { CustomerQueryParams } from '@aid/customers/types/classes/customer-query-params.class';
import { MembersService } from '@aid/members/services';
import {
  InsuranceTypeService,
  ProvidersService
} from '@aid/administration/services';
import { map, tap } from 'rxjs/operators';
import { Member } from '@aid/members/types/classes';
import { InsuranceType, Provider } from '@aid/administration/types/classes';
import { DistributorsService } from '@aid/partnerships/services';
import { Distributor } from '@aid/partnerships/types/classes';

@Injectable()
export class CustomersFilterService {
  private filter = new BehaviorSubject<CustomerFilters | null>(null);

  private sidenavOpened = new BehaviorSubject<boolean>(false);

  constructor(
    private providersService: ProvidersService,
    private insuranceTypeService: InsuranceTypeService,
    private membersService: MembersService,
    private distributorService: DistributorsService
  ) {}

  applyFilter(filter: CustomerFilters) {
    this.filter.next(filter);
  }

  get sidenavOpened$(): Observable<boolean> {
    return this.sidenavOpened.asObservable();
  }

  toggleSidenav() {
    this.sidenavOpened.next(!this.sidenavOpened.value);
  }

  get filtersValue(): CustomerFilters | null {
    return this.filter.value;
  }

  /**
   * Create Filter from Query Params
   * If the current filter has the same insuranceType, provider, or agent we take the them
   * from current filter
   * If not we get the details from the API
   */
  createFilterFromParams(
    params: CustomerQueryParams
  ): Observable<CustomerFilters> {
    const currentFilter = this.filtersValue;
    const filter = CustomerFilters.createFromParams(params, currentFilter);

    const observables: Observable<{
      key: string;
      value: Member | Provider | InsuranceType | Distributor;
    }>[] = [];

    if (params.supervisor && !filter.agent) {
      observables.push(
        this.membersService.get(params.supervisor).pipe(
          map(value => {
            return {
              key: 'agent',
              value
            };
          })
        )
      );
    }

    if (params.provider && !filter.provider) {
      observables.push(
        this.providersService.get(params.provider).pipe(
          map(value => {
            return {
              key: 'provider',
              value
            };
          })
        )
      );
    }

    if (params.insurance_type && !filter.insuranceType) {
      observables.push(
        this.insuranceTypeService.get(params.insurance_type).pipe(
          map(value => {
            return {
              key: 'insuranceType',
              value
            };
          })
        )
      );
    }

    if (params.distributor && !filter.distributor) {
      observables.push(
        this.distributorService.get(params.distributor).pipe(
          map(value => {
            return {
              key: 'distributor',
              value
            };
          })
        )
      );
    }

    if (observables.length > 0) {
      return combineLatest(observables).pipe(
        tap(values => {
          const agent = values.find(value => value.key === 'agent');
          filter.agent = agent ? (agent.value as Member) : null;

          const provider = values.find(value => value.key === 'provider');
          filter.provider = provider ? (provider.value as Provider) : null;

          const insuranceType = values.find(
            value => value.key === 'insuranceType'
          );
          filter.insuranceType = insuranceType
            ? (insuranceType.value as InsuranceType)
            : null;

          const distributor = values.find(value => value.key === 'distributor');
          filter.distributor = distributor
            ? (distributor.value as Distributor)
            : null;

          this.applyFilter(filter);
        }),
        map(() => filter)
      );
    } else {
      this.filter.next(filter);
      return of(filter);
    }
  }
}
